<template>
  <v-navigation-drawer
    :value="drawer"
    fixed
    :width="drawerWidth"
    class="black font-color--text pa-6 elevation-0"
    tabindex="0"
    stateless
    >
    <div ref="drawerHeader" class="drawer-header" v-sticky="{ zIndex: 5, stickyTop: drawerHeaderStickyTop }">
      <v-layout row>
        <v-flex>
          <div id="pageHeader" class="text--title-medium drawer-header--title"><i>{{ currentPage.title }}</i></div>
          <div v-if="currentPage.author" class="drawer-header--author">by {{ currentPage.author }}</div>
          <div v-if="currentPage.subtitle" class="text--title-italics-medium text--uppercase"><i>{{ currentPage.subtitle }}</i></div>
          <div class="drawer-actions mt-4">
            <print-button />
            <facebook-share-button />
            <twitter-share-button />
            <external-content-button v-if="currentPage.external_content_url" :url="currentPage.external_content_url"/>
          </div>
        </v-flex>
        <v-flex class="text-xs-right pl-6 drawer-back-container">
          <router-link class="drawer-back text--small no-underline" :to="getPath(backPage.url)" v-sticky="{ zIndex: 5, stickyTop: $store.getters.isChrome ?  20 : 0 }"><arrow reverse/><i class="ml-3">BACK</i></router-link>
        </v-flex>
        <v-flex class="text-xs-right pl-6 print-logo-container">
          <img class="print-logo" src="@/assets/images/logo.png">
        </v-flex>
      </v-layout>
      <hr class="mb-7"/>
    </div>
    <div class="drawer-content" ref="drawerContent" v-html="formatFootnotes(currentPage.text)" @click="onClickContent($event)"></div>
    <ul class="subpage-list subpage-list--full-width text--medium mt-5" v-if="subpages && subpages.length">
      <li v-for="subpage in subpages" v-if="subpage.visible" :key="subpage.uid" class="mb-6">
        <div v-if="subpage.cover_image" class="subpage--cover-image mb-2"><img :src="getImageUrl(subpage.cover_image, subpage)"/></div>
        <div class="text--title-medium"><i>{{ subpage.title }}</i></div>
        <div v-if="subpage.author" class="subpage--author">by {{ subpage.author }}</div>
        <div v-if="subpage.subtitle" class="text--title-italics-medium text--uppercase"><i>{{ subpage.subtitle }}</i></div>
        <router-link class="no-underline" :to="{ path: getPath(subpage.url) }"><arrow class="mr-2"/><i>READ</i></router-link>
        <hr/>
      </li>
    </ul>
  </v-navigation-drawer>
</template>
<script>
import FacebookShareButton from '@/components/FacebookShareButton'
import PrintButton from '@/components/PrintButton'
import TwitterShareButton from '@/components/TwitterShareButton'
import ExternalContentButton from '@/components/ExternalContentButton'
import VueSticky from 'vue-sticky'
import Arrow from '@/components/Arrow'
import { getPath, getImageUrl } from '@/util'

function disableScroll () {
  // Get the current page scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

  // if any scroll is attempted, set this to the previous value
  window.onscroll = function () {
    window.scrollTo(scrollLeft, scrollTop)
  }
}

function enableScroll () {
  window.onscroll = function () {}
}

export default {
  directives: {
    'sticky': VueSticky
  },
  props: {
    page: {
      type: Object
    },
    backPage: {
      type: Object
    }
  },
  components: {
    FacebookShareButton,
    PrintButton,
    TwitterShareButton,
    Arrow,
    ExternalContentButton
  },
  data () {
    return {
      drawer: false,
      drawerWidth: 300,
      drawerHeaderStickyTop: 0,
      currentPage: {},
      transitionDuration: 500
    }
  },
  computed: {
    subpages () {
      return (this.currentPage.children || [])
        .map(page => {
          if (page.type === 'existing_section') {
            return this.getExistingSection(page)
          }

          return page
        })
    }
  },
  watch: {
    page: {
      immediate: true,
      handler (newValue, oldValue) {
        this.animateDrawer(newValue, oldValue)
        this.toggleBodyScrolling(newValue, oldValue)
      }
    }
  },
  created () {
    window.addEventListener('resize', this.onResize)
    document.body.addEventListener('load', this.onImageLoad, true)
  },
  mounted () {
    this.$el.addEventListener('wheel', this.onWheel)
    this.$el.addEventListener('keydown', this.onKeydown, true)
    this.onResize()
    if (this.$route.hash) {
      setTimeout(() => {
        const element = this.getAnchorElement(this.$route.hash)
        if (!element) {
          return
        }

        this.scrollToElement(element)
      }, 300)
    }
  },
  destroyed () {
    this.$el.removeEventListener('wheel', this.onWheel)
    this.$el.removeEventListener('keydown', this.onKeydown, true)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    toggleBodyScrolling (newValue, oldValue) {
      if (!newValue || !newValue.uid) {
        enableScroll()
        return
      }

      setTimeout(() => {
        disableScroll()
      }, this.transitionDuration)
    },
    animateDrawer (newValue, oldValue) {
      if (!newValue || !newValue.uid) {
        this.drawer = false
        setTimeout(() => {
          this.currentPage = {}
        }, this.transitionDuration)
        return
      }

      if (newValue.uid) {
        this.calculateDrawerHeaderStickyTop()
        // if we're navigating within drawer, act like it's leaving
        if (oldValue && oldValue.uid && newValue.uid !== oldValue.uid) {
          this.drawer = false
          setTimeout(() => {
            this.currentPage = newValue
            this.drawer = true
            this.$nextTick(() => {
              this.$el.scrollTo(0, 0)
              this.styleFootnotes()
            })
          }, this.transitionDuration)
        } else {
          this.currentPage = newValue
          this.drawer = true
          this.$nextTick(() => {
            this.$el.scrollTo(0, 0)
            this.styleFootnotes()
          })
        }
      }
    },
    getExistingSection (existingSectionPage) {
      return this.$store.getters.getPageByID(existingSectionPage.existing_section)
    },
    preventScroll (event) {
      event.stopPropagation()
      event.preventDefault()
      event.returnValue = false
      return false
    },
    // Don't let scroll move out of drawer
    // TODO for touch
    // http://jsfiddle.net/TroyAlford/4wrxq/4/
    onWheel (event) {
      const scrollTop = this.$el.scrollTop
      const scrollHeight = this.$el.scrollHeight
      const height = this.$el.clientHeight
      const delta = event.deltaY
      const up = delta < 0

      if (!up && delta > scrollHeight - height - scrollTop) {
        // Scrolling down, but this will take us past the bottom.
        this.$el.scrollTo(0, scrollHeight)
        return this.preventScroll(event)
      } else if (up && -delta > scrollTop) {
        // Scrolling up, but this will take us past the top.
        this.$el.scrollTo(0, 0)
        return this.preventScroll(event)
      }
    },
    // don't let scroll move out of drawer when using arrow keys
    // 40 is the number of pixels moved in Chrome
    onKeydown (event) {
      switch (event.which) {
        // down arrow
        case 40:
          event.deltaY = 40
          return this.onWheel(event)
        // up arrow
        case 38:
          event.deltaY = -40
          return this.onWheel(event)
        default:
      }
    },
    getImageUrl,
    getPath,
    styleFootnotes () {
      const footnotes = this.$refs.drawerContent.querySelectorAll('[href*="#ftnt_"]')
      let i = footnotes.length
      while (i--) {
        const footnote = footnotes[i]
        footnote.parentElement.classList.add('footnotes')
      }
    },
    formatFootnotes (text) {
      if (!text) {
        return ''
      }

      // remove domain from footnotes
      return text.replace(/[^"]*(#ftnt[^"]*)/g, '$1')
    },
    calculateDrawerHeaderStickyTop () {
      this.$nextTick(() => {
        let adjust = 5
        // NB: For Chrome versions at the time of this
        // following code's writing, sticky positioning
        // was borked and this fixed it. Maybe around
        // version 65-68?
        if (this.$store.getters.isChrome) {
          // adjust = 60
          if (this.$vuetify.breakpoint.smAndDown) {
            adjust = 25
          }
        }

        const mobileAdjust = this.$store.getters.isIOS ? 35 : 0
        this.drawerHeaderStickyTop = -1 * this.$refs.drawerHeader.offsetHeight + adjust + mobileAdjust
      })
    },
    onResize () {
      const content = document.querySelector('.content-column')
      const contentRect = content.getBoundingClientRect()
      if (this.$vuetify.breakpoint.smAndDown) {
        const width = window.innerWidth ||
           document.documentElement.clientWidth ||
           document.body.clientWidth
        this.drawerWidth = width
      } else {
        this.drawerWidth = contentRect.right - 60
      }

      this.calculateDrawerHeaderStickyTop()
    },
    onClickContent (event) {
      const target = this.findAncestor(event.target, 'a')
      if (!target) {
        return true
      }

      const url = target.href
      const hash = (new URL(url)).hash
      if (!hash) {
        return true
      }

      const element = this.getAnchorElement(hash)
      if (!element) {
        return true
      }

      this.scrollToElement(element)
    },
    findAncestor (el, sel) {
      while (el && !((el.matches || el.matchesSelector || el.msMatchesSelector).call(el, sel))) {
        el = el.parentElement
      }

      return el
    },
    getAnchorElement (hash) {
      let targetSelector = hash.substring(1)
      // normal footnote target
      let element = document.getElementById(targetSelector)
      // try new footnote format
      if (!element) {
        targetSelector = hash.replace(/(ftnt)(\d+)/, '$1_ref$2')
        element = document.querySelector(`[href*="${targetSelector}"]`)
      }

      return element
    },
    scrollToElement (element) {
      // NB: I would really like to use fragments how they are supposed to be used,
      // but this navigationm drawer component is giving me some hell when navigating to
      // a path with a hash
      // if (element) {
      let offset = -1 * 36 * 2.5
      if (this.$store.state.app.phone) {
        offset = -1 * 18 * 2.5
      }

      if (this.$store.state.app.tablet) {
        offset = -1 * 30 * 2.5
      }
      this.$scrollTo(element, 500, { container: '.v-navigation-drawer', offset })
      // }
    }
  }
}
</script>
<style lang="stylus">
@require '~vuetify/src/stylus/settings/_variables.styl'
@require '../assets/styles/base'

.print-logo-container
  display: none;
  justify-content: flex-end;
  align-content: center;
  align-items: center;
  @media print
    display flex

.print-logo
  width: 32px;
  height: 32px;
  margin-right: 2px;

.subpage-list.subpage-list--full-width
  width 100%
  max-width none

.drawer-page-header
  position: sticky
  width: 100%
  top: -130px
  background-color: black

.drawer-content
  // fix for / enabler for simple captions with B in markdown text field
  // http://continentcontinent.bodyofus.com/archives/projects/publish-and-be-damned
  img ~ strong
      position: relative;
      top: -10px;
  // fix for some inofficiall cover images being aligned right
  // http://continentcontinent.bodyofus.com/archives/issues/issue-8-1-2-2019
  & > figure
    text-align left
  @media print
    font-size 0.68rem
    column-count 2
    column-gap 2.0rem
    column-fill auto
    p, h1, h2, h3, h4, div, blockquote, ul, ol, em, table, figure
      margin-bottom 1rem
    em
      font-style normal
    blockquote
      display block
      margin 0 auto 1rem auto
      text-indent 2rem
    .footnotes
      ol, p
        max-width 100%
    figure
      margin 0 0 1rem 0
      img
        display: block;

.drawer-actions
  margin-bottom -10px
.drawer-header
  background-color rgba(0, 0, 0, 0.8)
  .drawer-header--title
    font-size 1.05rem
    line-height 1.05
  .drawer-header--author
    line-height 1.05
    padding-top 2px
    padding-bottom 3px
  @media print
    position: static !important;
    hr
      border-width: thin;
.drawer-back-container
  min-width 225px
  .drawer-back
    display block
  @media $display-breakpoints.sm-and-down
    min-width 155px
  @media print
    display none
.v-navigation-drawer.v-navigation-drawer--open
  @media print
    overflow-y visible !important
    width 100% !important
    height auto !important
    position static !important
    padding 0 !important
    background-color white !important
    color black !important
    .drawer-header
      background-color white !important
    .drawer-actions,
    .drawer-back
      display none
    ~ .container
      display none
</style>
